import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getSphereGrowth } from '../api/transactions';

/**
 * L-76 (Daniel 2026-07-04): sphere-growth accountability tile.
 *
 * Build 96 (Daniel 2026-10-04): Mets lead now. "i dont care that we have
 * 1000 people i dont know id rather have 100 that know me and are Mets."
 *
 * One row per agent: the Mets in their database (the headline and the bar),
 * the total contacts beside it for scale, and the stage adds for the window
 * as the small number. Each number opens the people behind it. Database
 * definition: lib/contact-database.ts (vendors left out, one owner each).
 */
export default function SphereGrowthCard() {
  const [win, setWin] = useState<'this_month' | 'last_month' | 'ytd'>('this_month');
  const { data, isLoading } = useQuery({
    queryKey: ['sphere-growth', win],
    queryFn: () => getSphereGrowth(win),
    staleTime: 60_000,
  });

  const agents = data?.agents ?? [];
  const maxMets = Math.max(1, ...agents.map((a) => a.mets ?? 0));
  const winLabel = win === 'this_month' ? 'this month' : win === 'last_month' ? 'last month' : 'this year';
  const numLink: React.CSSProperties = { color: 'inherit', textDecoration: 'none' };

  return (
    <section style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '18px 20px',
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-heading)', margin: 0 }}>
            Database: Mets
          </h2>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            People who know us, out of everyone in the database
            {data && (
              <span style={{ marginLeft: 6, color: 'var(--text-heading)', fontWeight: 500 }}>
                · <Link to="/contacts?db=team&contact_status=met" style={numLink}>{data.total_mets} Mets</Link>
                {' of '}
                <Link to="/contacts?db=team" style={numLink}>{data.total_contacts}</Link>
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['this_month', 'last_month', 'ytd'] as const).map((k) => {
            const active = win === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setWin(k)}
                title="Window for the stage adds"
                style={{
                  padding: '3px 10px',
                  fontSize: 11,
                  fontWeight: active ? 600 : 500,
                  borderRadius: 999,
                  border: active ? 'none' : '1px solid var(--border)',
                  background: active ? 'var(--brand-primary, #4f46e5)' : 'transparent',
                  color: active ? 'white' : 'var(--text-muted)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: '0.02em',
                }}
              >
                {k === 'this_month' ? 'This month' : k === 'last_month' ? 'Last month' : 'YTD'}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Loading</p>
      ) : agents.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>No agents on the team yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {agents.map((a) => {
            const name = [a.first_name, a.last_name].filter(Boolean).join(' ');
            const mets = a.mets ?? 0;
            const contacts = a.contacts ?? 0;
            const pct = Math.max(2, (mets / maxMets) * 100);
            const share = contacts > 0 ? Math.round((mets / contacts) * 100) : null;
            return (
              <div
                key={a.user_id}
                style={{ display: 'grid', gridTemplateColumns: '120px 1fr auto', gap: 10, alignItems: 'center', padding: '6px 8px' }}
              >
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-heading)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {name}
                </div>
                <div style={{ height: 8, background: 'var(--bg-surface-2, #F5F5F5)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${pct}%`,
                    background: mets === 0 ? 'transparent' : 'var(--brand-primary, #4f46e5)',
                    borderRadius: 999, transition: 'width 200ms',
                  }} />
                </div>
                <div style={{ fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  <Link to={`/contacts?db=${a.user_id}&contact_status=met`} style={{ ...numLink, fontSize: 13, fontWeight: 600, color: mets === 0 ? 'var(--text-muted)' : 'var(--text-heading)' }} title="Open this agent's Mets">
                    {mets} Mets
                  </Link>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {' of '}
                    <Link to={`/contacts?db=${a.user_id}`} style={numLink} title="Everyone in this agent's database">{contacts}</Link>
                    {share !== null ? ` (${share}%)` : ''}
                    {' · '}
                    <Link to={`/contacts/nurture?owner=${a.user_id}`} style={numLink} title={`Moved into Past Client, Sphere or Warm ${winLabel}`}>+{a.count}</Link>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 10, color: 'var(--text-muted)' }}>
        A Met is a contact marked Met, someone who knows the agent. Vendors are left out. The +number is contacts moved into Past Client, Sphere or Warm {winLabel}.
      </div>
    </section>
  );
}
